import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Quote } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import useLenis from "@/hooks/useLenis";
import api, { mediaUrl } from "@/lib/api";
import { AdSidebar } from "@/pages/Home";

export default function StoryPage() {
  useLenis();
  const { id } = useParams();
  const [story, setStory] = useState(null);
  const [settings, setSettings] = useState(null);
  const [ads, setAds] = useState([]);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/stories/public/${id}`).then((r) => setStory(r.data)).catch(() => setError(true));
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/content?type=ad&visible=true").then((r) => setAds(r.data)).catch(() => {});
  }, [id]);

  const media = story?.media_id ? mediaUrl({ media_id: story.media_id }) : null;

  return (
    <div className="bg-imi-bg text-imi-text min-h-screen">
      <Header />
      <main className="pt-28 pb-24 min-h-[60vh]">
        <div className="max-w-[1180px] mx-auto px-6 md:px-10 grid lg:grid-cols-[1fr_300px] gap-10">
          <div className="max-w-[820px] w-full">
            <Link to="/#voices" data-testid="back-voices" className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.15em] text-imi-text/50 hover:text-imi-gold transition-colors mb-10">
              <ArrowLeft size={16} /> All voices
            </Link>

            {error && <div className="py-24 text-center text-imi-text/40">This story is not available.</div>}

            {story && (
              <article data-testid="story-detail">
                <div className="flex items-center gap-3 mb-6">
                  <span className="text-[10px] uppercase tracking-[0.25em] text-imi-gold border border-imi-gold/30 px-3 py-1">{story.format}</span>
                </div>
                <h1 className="font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight mb-8">
                  A voice from <span className="italic gold-gradient-text">the inside</span>
                </h1>

                {media && story.media_type?.startsWith("video") && (
                  <video controls preload="metadata" src={media} className="w-full border border-imi-border mb-8" />
                )}
                {media && story.media_type?.startsWith("audio") && (
                  <div className="border border-imi-border bg-imi-surface p-6 mb-8"><audio controls src={media} className="w-full" /></div>
                )}

                <Quote size={40} className="text-imi-gold/40 mb-4" />
                {story.story && (
                  <p className="text-imi-text/80 text-lg md:text-xl leading-relaxed whitespace-pre-line">{story.story}</p>
                )}
                <div className="mt-10 pt-6 border-t border-imi-border font-serif text-2xl text-imi-goldlight">— {story.alias}</div>

                <div className="mt-14">
                  <Link to="/share" className="inline-flex items-center gap-2 bg-imi-gold text-imi-bg px-7 py-4 text-sm uppercase tracking-[0.15em] font-semibold hover:bg-imi-goldlight transition-colors">
                    Share your story
                  </Link>
                </div>
              </article>
            )}
          </div>

          {ads.length > 0 && (
            <div className="lg:sticky lg:top-28 lg:self-start">
              <AdSidebar items={ads} title="Support the mission" />
            </div>
          )}
        </div>
      </main>
      <Footer settings={settings} />
    </div>
  );
}
